import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { LeadConversionData } from '@/hooks/useAnalytics';

interface LeadConversionChartProps {
  data: LeadConversionData[];
}

const chartConfig = {
  new: {
    label: 'New',
    color: 'hsl(var(--muted-foreground))',
  },
  contacted: {
    label: 'Contacted',
    color: 'hsl(var(--primary))',
  },
  qualified: {
    label: 'Qualified',
    color: 'hsl(var(--accent-foreground))',
  },
  converted: {
    label: 'Converted',
    color: 'hsl(217 91% 60%)',
  },
  lost: {
    label: 'Lost',
    color: 'hsl(var(--destructive))',
  },
};

export function LeadConversionChart({ data }: LeadConversionChartProps) {
  const getStatusColor = (status: string) => {
    const statusKey = status.toLowerCase();
    return chartConfig[statusKey as keyof typeof chartConfig]?.color || 'hsl(var(--muted-foreground))';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Lead Conversion Funnel
        </CardTitle>
        <CardDescription>
          Distribution of leads by conversion status
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ status, percentage }) => `${status}: ${percentage}%`}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getStatusColor(entry.status)} 
                  />
                ))}
              </Pie>
              <ChartTooltip 
                content={<ChartTooltipContent 
                  formatter={(value, name) => [value, name]}
                  labelFormatter={(label) => `Status: ${label}`}
                />}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
        <div className="flex flex-wrap gap-4 mt-4">
          {data.map((item) => (
            <div key={item.status} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: getStatusColor(item.status) }}
              />
              <span className="text-sm text-muted-foreground">
                {item.status}: {item.count} ({item.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}